import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
  Select,
  MenuItem,
} from "@material-ui/core";
import { TextField } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import CountryPicker, { CountryType } from "../signup/CountryPicker";
import {
  Region,
  Municipality,
  dateFormat,
  emailRegex,
  passwordRegex,
  getRegions,
  getMunicipalities,
} from "../../constants";
import { useForm } from "react-hook-form";
import "date-fns";
import { add, parse, isBefore, isAfter, isValid } from "date-fns";
import DateFnsUtils from "@date-io/date-fns";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { SignUpData } from "../../firebase/users";
import Button from "../shared/Button";
import { colors } from "../../theme";

interface Props {
  save: (user: SignUpData) => void;
  initialValues?: SignUpData;
  showTermsCheckbox?: boolean;
  showEmailField?: boolean;
  showPasswordField?: boolean;
  submitButtonText?: string;
}

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  submitContainer: {
    textAlign: "center",
    [theme.breakpoints.down("xs")]: {
      marginTop: "35px",
    },
  },
  submit: {
    width: "140px",
    margin: "0 auto",
  },
  textField: {
    width: "250px",
  },
  error: {
    color: colors.ERROR,
  },
  url: {
    color: colors.URL,
    fontWeight: 500,
  },
  span: {
    fontSize: "21px",
  },
  signin: {
    textAlign: "center",
    marginTop: "25px",
  },
  input: {
    height: "70px",
    [theme.breakpoints.down("xs")]: {
      height: "auto",
    },
  },
  profileEmail: {
    height: "50px",
    [theme.breakpoints.down("xs")]: {
      height: "auto",
    },
  },
}));

const newUser = {
  firstname: "",
  lastname: "",
  email: "",
  password: "",
  continent: "",
  country: "",
  region: "",
  municipality: "",
  birthdate: "",
  groupaccount: false,
  groupname: "",
  parentsPermission: false,
  agreeterms: false,
};

export default function Form({
  save,
  initialValues,
  showTermsCheckbox = true,
  showEmailField = true,
  showPasswordField = true,
  submitButtonText,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  // const [currentPassword, setCurrentPassword] = useState<string>("");
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);

  const initialFocusedDate = add(new Date(), { years: -16 });

  // Keeping the form's state and doing validation is done with react-hook-form's useForm hook.
  const {
    register,
    handleSubmit,
    errors,
    setValue,
    triggerValidation,
  } = useForm<SignUpData>({
    defaultValues: initialValues ? initialValues : newUser,
  });

  const [isGroupAccount, setIsGroupAccount] = useState<boolean>(
    initialValues ? initialValues.groupaccount : false
  );
  const [parentsPermission, setParentsPermission] = useState<boolean>(
    initialValues ? initialValues.parentsPermission : false
  );
  const [needPermissionUnder16, setNeedPermissionUnder16] = useState<boolean>();

  // Birthdate and Country have their own state because the current value can not be retrieved
  // from react-hook-form.
  const [birthdate, setBirthdate] = useState<string | null>(
    initialValues ? initialValues.birthdate : null
  );

  const [country, setCountry] = useState<string>(
    initialValues ? initialValues.country : ""
  );
  const [regions, setRegions] = useState<Region[]>([]);
  const [region, setRegion] = useState<string>(
    initialValues?.region ? initialValues.region : ""
  );
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [municipality, setMunicipality] = useState<string>(
    initialValues?.municipality ? initialValues.municipality : ""
  );

  // Password functions
  // function handleCurrentPasswordChange(currentPassword: string) {
  //   // setCurrentPassword(currentPassword);
  //   setValue("currentPassword", currentPassword);
  //   triggerValidation("currentPassword");
  // }
  const toggleShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  // To register custom components for react-hook-form a useEffect is used.
  useEffect(() => {
    // Birthdate can not be more than 120 years ago or after today.
    register(
      { name: "birthdate" },
      {
        validate: (value) => {
          if (!value) return false;

          const birthdate = parse(value, dateFormat, new Date());
          return (
            isValid(birthdate) &&
            !isBefore(birthdate, add(new Date(), { years: -120 })) &&
            !isAfter(birthdate, new Date())
          );
        },
      }
    );

    register({ name: "continent" }, { required: true });
    register({ name: "country" }, { required: true });
    register({ name: "region" }, { required: false });
    register({ name: "municipality" }, { required: false });
  }, [register]);

  // Populate the regions selectbox when a country is selected.
  useEffect(() => {
    if (country && country !== "") {
      setRegions(getRegions(country));
    } else {
      setRegions([]);
    }
  }, [country]);

  // Populate the municipalities when a region is selected.
  useEffect(() => {
    if (region && region !== "") {
      setMunicipalities(getMunicipalities(region));
    } else {
      setMunicipalities([]);
    }
  }, [region]);

  useEffect(() => {
    setValue("region", region);
  }, [region, setValue]);

  useEffect(() => {
    setValue("municipality", municipality);
  }, [municipality, setValue]);

  // Set the value of birthdate with an onChange event, because it comes from a custom component.
  function handleBirthdateChange(
    date: MaterialUiPickersDate,
    value?: string | null | undefined
  ) {
    if (!value) return;

    setValue("birthdate", value);
    setBirthdate(value);

    // Somehow validation of birthdate is only triggered on submit, so it is triggered here manually.
    triggerValidation("birthdate");
  }

  // When birthdate changes, determine the user needs permission from it's parents.
  useEffect(() => {
    if (!birthdate) return;
    const parsedBirthdate = parse(birthdate, dateFormat, new Date());
    setNeedPermissionUnder16(
      isValid(parsedBirthdate) &&
        isAfter(parsedBirthdate, add(new Date(), { years: -16 }))
    );
  }, [birthdate]);

  // Because selecting countries, regions and municipalities is custom functionality, update the state manually via this onChange handler.
  function handleCountryChange(value: CountryType) {
    setValue("country", value.country);
    setValue("continent", value.continent);
    setCountry(value.country);
    setRegion("");
    setMunicipality("");
    getRegions(value.country);
    // Somehow validation of country is only triggered on submit, so it is triggered here manually.
    triggerValidation("country");
  }

  const handleRegionChange = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>
  ) => {
    if (!event.target.value) return;
    const value = event.target.value as string;
    setValue("region", value);
    setRegion(value);
    setMunicipality("");
    getMunicipalities(value);
    triggerValidation("region");
  };

  const handleMunicipalityChange = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>
  ) => {
    if (!event.target.value) return;
    const value = event.target.value as string;
    setValue("municipality", value);
    setMunicipality(value);
    triggerValidation("municipality");
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  function handleParentsPermissionChange() {
    setParentsPermission(!parentsPermission);
    setValue("parentsPermission", !parentsPermission);
  }

  function handleIsGroupAccountChange() {
    setIsGroupAccount(!isGroupAccount);
    setValue("groupaccount", !isGroupAccount);
  }

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit(save)}>
      <Grid item container md={12} spacing={2}>
        <Grid container item sm={6} xs={12} spacing={2}>
          <span className={classes.span}>{t("signup.userInfo")}</span>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              id="firstname"
              name="firstname"
              label={t("signup.firstname")}
              autoComplete="fname"
              autoFocus
              required
              inputRef={register({
                required: true,
                validate: {
                  maxlength: (value: string) => value.length <= 120,
                },
              })}
              error={errors.firstname ? true : false}
              helperText={
                errors.firstname
                  ? errors.firstname.type === "maxlength"
                    ? t("signup.error_maxlength", { max: 120 })
                    : t("signup.firstname_required")
                  : null
              }
              className={classes.textField}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              variant="outlined"
              id="lastname"
              name="lastname"
              label={t("signup.lastname")}
              autoComplete="lname"
              required
              inputRef={register({
                required: true,
                validate: {
                  maxlength: (value: string) => value.length <= 120,
                },
              })}
              error={errors.lastname ? true : false}
              helperText={
                errors.lastname
                  ? errors.lastname.type === "maxlength"
                    ? t("signup.error_maxlength", { max: 120 })
                    : t("signup.lastname_required")
                  : null
              }
              className={classes.textField}
            />
          </Grid>

          <Grid item xs={12}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                inputVariant="outlined"
                id="birthdate"
                name="birthdate"
                margin="none"
                label={t("signup.birthdate")}
                value={birthdate}
                required
                onChange={handleBirthdateChange}
                format={dateFormat}
                disableFuture={true}
                initialFocusedDate={initialFocusedDate}
                error={errors.birthdate ? true : false}
                helperText={
                  errors.birthdate ? t("signup.birthdate_valid") : null
                }
                style={{ width: "250px" }}
              />
            </MuiPickersUtilsProvider>
          </Grid>

          <Grid item xs={12}>
            <CountryPicker
              country={country}
              selectCountry={handleCountryChange}
              error={errors.country ? t("signup.country_required") : null}
            />
          </Grid>

          {regions.length > 0 && (
            <Grid item xs={12}>
              <FormControl variant="outlined" style={{ width: "250px" }}>
                <InputLabel id="region-label">{t("signup.region")}</InputLabel>
                <Select
                  labelId="region-label"
                  id="region"
                  label={t("signup.region")}
                  name="region"
                  onChange={handleRegionChange}
                  value={region}
                >
                  {regions.map((region) => (
                    <MenuItem
                      key={region.region}
                      value={region.region}
                      style={{ backgroundColor: "transparent" }}
                    >
                      {region.region}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {municipalities.length > 0 && (
            <Grid item xs={12}>
              <FormControl variant="outlined" style={{ width: "250px" }}>
                <InputLabel id="municipality-label">
                  {t("signup.municipality")}
                </InputLabel>
                <Select
                  labelId="municipality-label"
                  id="municipality"
                  label={t("signup.municipality")}
                  name="municipality"
                  onChange={handleMunicipalityChange}
                  value={municipality}
                >
                  {municipalities.map((municipality) => (
                    <MenuItem
                      key={municipality.municipality}
                      value={municipality.municipality}
                      style={{ backgroundColor: "transparent" }}
                    >
                      {municipality.municipality}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
        <Grid
          container
          item
          sm={6}
          xs={12}
          spacing={2}
          className={classes.profileEmail}
        >
          <span className={classes.span}>{t("signup.userAccount")}</span>
          {showEmailField && (
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                id="email"
                name="email"
                label={t("signup.email")}
                autoComplete="email"
                inputRef={register({
                  pattern: emailRegex,
                  validate: {
                    maxlength: (value: string) => value.length <= 255,
                  },
                })}
                error={errors.email ? true : false}
                helperText={
                  errors.email
                    ? errors.email.type === "maxlength"
                      ? t("signup.error_maxlength", { max: 255 })
                      : t("signup.email_valid")
                    : null
                }
                className={classes.textField}
              />
            </Grid>
          )}

          {/* <ChangePassword /> */}
          <span className={classes.span}>{t("profile.changepassword")}</span>
          <Grid item xs={12} className={classes.input}>
            <FormControl className={classes.textField} variant="outlined">
              <InputLabel htmlFor="currentpassword">
                <span className={errors.currentPassword && classes.error}>
                  {t("currentpassword.currentpassword")}
                </span>
              </InputLabel>
              <OutlinedInput
                id="currentPassword"
                name="currentPassword"
                type={showPassword ? "text" : "password"}
                inputRef={register({
                  pattern: passwordRegex,
                })}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={toggleShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      style={{ backgroundColor: "transparent" }}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                labelWidth={112}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} className={classes.profileEmail}>
            <FormControl className={classes.textField} variant="outlined">
              <InputLabel htmlFor="newPassword">
                <span className={errors.newPassword && classes.error}>
                  {t("changepassword.newpassword")}
                </span>
              </InputLabel>
              <OutlinedInput
                id="newPassword"
                placeholder={t("changepassword.newpassword")}
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                inputRef={register({
                  pattern: passwordRegex,
                })}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={toggleShowNewPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      style={{ backgroundColor: "transparent" }}
                    >
                      {showNewPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                labelWidth={112}
              />
              {errors.newPassword && (
                <FormHelperText className={classes.error}>
                  {t("changepassword.password_valid")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </Grid>

      <Grid container item sm={12} className={classes.submitContainer}>
        <Button type="submit" className={classes.submit}>
          {submitButtonText ? submitButtonText : t("signup.submit")}
        </Button>
      </Grid>
    </form>
  );
}
